import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ClimateQuestion {
  key: string;
  dimension: string;
  text: string;
}

interface PendingResponse {
  question: ClimateQuestion | null;
  privacyNote: string;
}

/** Aplazar con "Ahora no" silencia el pulso el resto del dia (no se insiste en cada pausa). */
const SNOOZE_KEY = 'sb_climate_snooze';

/**
 * Termometro de clima: una pregunta quincenal de 1 toque, anonima.
 * El servidor decide si toca preguntar (empresa + quincena sin responder); aqui solo se muestra.
 */
@Injectable({ providedIn: 'root' })
export class ClimatePulseService {
  private readonly http = inject(HttpClient);
  private readonly questionSubject = new BehaviorSubject<ClimateQuestion | null>(null);
  private checking = false;

  readonly question$ = this.questionSubject.asObservable();
  privacyNote = '';

  private today(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private snoozedToday(): boolean {
    try {
      return localStorage.getItem(SNOOZE_KEY) === this.today();
    } catch {
      return false;
    }
  }

  /** Consulta si hay pregunta pendiente y, si la hay, la muestra. Silencioso ante cualquier error. */
  async promptIfDue(): Promise<void> {
    if (this.checking || this.questionSubject.value || this.snoozedToday()) return;
    this.checking = true;
    try {
      const res = await firstValueFrom(this.http.get<PendingResponse>(`${environment.apiUrl}/climate/pending`));
      this.privacyNote = res.privacyNote;
      if (res.question) this.questionSubject.next(res.question);
    } catch {
      // El pulso es opcional: un fallo de red no debe molestar a la persona.
    } finally {
      this.checking = false;
    }
  }

  async answer(rating: number): Promise<void> {
    const q = this.questionSubject.value;
    if (!q) return;
    await firstValueFrom(this.http.post(`${environment.apiUrl}/climate/respond`, { questionKey: q.key, rating }));
  }

  /** Cierra el modal. `snooze` = "Ahora no": no vuelve a aparecer hoy. */
  dismiss(snooze: boolean): void {
    if (snooze) {
      try {
        localStorage.setItem(SNOOZE_KEY, this.today());
      } catch {
        /* sin almacenamiento: se mostrara de nuevo, no es grave */
      }
    }
    this.questionSubject.next(null);
  }

  reset(): void {
    this.questionSubject.next(null);
  }
}
